import { useForm } from "react-hook-form";
import { CircleAlert, PlusCircle } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import FilePond from "@/components/ui/file-pond";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import ImageUploadPreview from "@/components/ui/image-upload-preview";

import {
  candidateProjectFormSchema,
  CandidateProjectFormSchema,
} from "./schema";
import { IProject } from "@/redux/features/candidateProfileSlice";

import { useAddUpdateProject } from "@/mutations/candidate/profile/project/add-update-project";
import { useDeleteProject } from "@/mutations/candidate/profile/project/delete-project";

type Props =
  | {
      profileId: string;
      projectId: string;
      project: IProject;
    }
  | {
      profileId: string;
      projectId?: never;
      project?: never;
    };

const CandidateProjectFormDialog = ({
  profileId,
  projectId,
  project,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { addProject, updateProject, isUpdatingProject } = useAddUpdateProject(
    profileId,
    () => setIsOpen(false),
  );
  const { deleteProject, isDeletingProject } = useDeleteProject(
    profileId,
    projectId ?? "",
    () => setIsOpen(false),
  );

  const form = useForm<CandidateProjectFormSchema>({
    resolver: zodResolver(candidateProjectFormSchema),
    mode: "onChange",
    defaultValues: {
      title: project?.title ?? "",
      summary: project?.summary ?? "",
      link: project?.link ?? "",
    },
  });

  const { link, project: projectFile } = form.watch();

  useEffect(() => {
    form.reset({
      title: project?.title ?? "",
      summary: project?.summary ?? "",
      link: project?.link ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, project]);

  const onSubmit = (data: CandidateProjectFormSchema) => {
    if (project && projectId) {
      updateProject({ data, projectId });
    } else {
      addProject({ data });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {project ? (
          <Button variant="link" className="edit-text self-end">
            Edit
          </Button>
        ) : (
          <Button variant="secondary" className="w-full">
            <PlusCircle />
            Add A Project
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>
              {project ? "Edit Project" : "Create A Project"}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            {project ? "Edit" : "Create"} your project below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Case Study Title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Summary</FormLabel>
                    <FormControl>
                      <Input placeholder="Summary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="image"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cover Picture</FormLabel>
                    <ImageUploadPreview
                      newImageFile={field.value}
                      oldImageLink={project?.imageLink}
                    />
                    <FormControl>
                      <FilePond
                        value={field.value ? [field.value] : []}
                        accept={"image/*"}
                        maxFileSizeKiloBytes={10240}
                        onChange={(files) => {
                          field.onChange(files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <br />
              <FormField
                control={form.control}
                name="link"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Please provide a link to your project</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="www.mywebsite.com/case_study"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="text-lg text-center">OR</p>
              <FormField
                control={form.control}
                name="project"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Upload Your Project</FormLabel>
                    <span className="flex flex-row gap-0.5 text-xs text-gray-600">
                      Maximum file size: 5MB
                      <Tooltip
                        content="If you need to share a larger project, consider
                          uploading it to a file hosting platform like OneDrive,
                          Dropbox, or Google Drive and sharing a link to that
                          file."
                      >
                        <CircleAlert className="cursor-pointer" size={12} />
                      </Tooltip>
                    </span>
                    <FormControl>
                      <FilePond
                        value={field.value ? [field.value] : []}
                        accept={"*"}
                        maxFileSizeKiloBytes={5120}
                        onChange={(files) => {
                          field.onChange(files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                {project && (
                  <Button
                    variant="destructive"
                    className="w-fit"
                    type="submit"
                    disabled={isDeletingProject || isUpdatingProject}
                    onClick={(e) => {
                      e.preventDefault();
                      deleteProject();
                    }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid ||
                    isDeletingProject ||
                    isUpdatingProject ||
                    // If this is an update
                    // Or if we don't have a link or project
                    (!project && !link && !projectFile)
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateProjectFormDialog;
